import { create } from "zustand";
import axios from "axios";
import { toast } from "sonner";
import { getAxiosErorrMessage, getUnexpiredToken } from "@/utils/utils";
import { fetchFileTree } from "../codebaseSlice";
import { SignedInSessionResource } from "@clerk/types";

interface ActivityState {
  restoringActivityId: string | null;

  restoreActivity: (
    dispatch: any,
    canvasId: string,
    activityId: string,
    activityStreamList: any[],
    triggerRefetchActivityStream: () => void,
    session: SignedInSessionResource
  ) => Promise<void>;
}

// Create the Zustand store with a more structured and type-safe approach
const useActivityStore = create<ActivityState>((set, get) => ({
  restoringActivityId: null,

  restoreActivity: async (
    dispatch: any,
    canvasId: string,
    activityId: string,
    activityStreamList: any[],
    triggerRefetchActivityStream: () => void,
    session: SignedInSessionResource
  ) => {
    set({ restoringActivityId: activityId });

    if (!session) {
      return;
    }

    const matchingActivityIndex = activityStreamList.findIndex(
      (activity) => activity.id === activityId
    );
    const matchingActivity = activityStreamList[matchingActivityIndex];

    if (!matchingActivity) {
      toast.error("Activity not found, please refresh the page");
      return;
    }

    // If the activity is undone, we need to redo it
    const isUndo = !matchingActivity.change_undone;

    const body = {};
    if (isUndo) {
      body["undoRestoreActivityId"] = matchingActivity.id;
    } else {
      body["redoRestoreActivityId"] = matchingActivity.id;
    }

    await axios
      .post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/parseAndMutate/activities/${
          isUndo ? "undoChangeToFiles" : "redoChangeToFiles"
        }`,
        body,
        {
          headers: {
            Authorization: `Bearer ${await getUnexpiredToken(session)}`,
          },
        }
      )
      .then(async (res) => {
        triggerRefetchActivityStream();
        dispatch(
          fetchFileTree({
            session,
            canvasId,
          })
        );
      })
      .catch((err) => {
        console.error(err);
        toast.error(
          `Failed to restore activity, please refresh the page : ${getAxiosErorrMessage(
            err
          )}`
        );
      })
      .finally(() => {
        set({ restoringActivityId: null });
      });
  },
}));

export default useActivityStore;
